import getApiBase from "../components/getApiBase";
import { isVercelFeatureBranchDeploy } from "./vercelEnv";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export { isVercelFeatureBranchDeploy };

/** @deprecated Use isVercelFeatureBranchDeploy */
export function usesStripeCheckoutSyncFallback() {
  return isVercelFeatureBranchDeploy();
}

export const PREVIEW_BILLING_WEBHOOK_NOTICE =
  "Stripe webhooks are not delivered on this feature-branch deploy. After checkout we update billing from the Stripe return URL. To validate real webhooks, test on preprod or production.";

export async function startBillingSetupSession({
  mechanicId,
  shopId,
  returnPath = "/mechanic-profile",
}) {
  const response = await fetch(`${getApiBase()}/api/stripe/create-setup-session`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify({ mechanicId, shopId, returnPath }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || "Failed to start payment setup");
  }

  if (data.alreadyReady) {
    return { alreadyReady: true, message: data.message };
  }

  sessionStorage.removeItem("billing_setup_return_failed");

  if (!data.url) {
    throw new Error("No checkout URL returned");
  }

  window.location.href = data.url;
  return { redirecting: true };
}

export async function syncBillingSetupSession(sessionId, mechanicId, shopId) {
  try {
    const response = await fetch(`${getApiBase()}/api/stripe/sync-setup-session`, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({ sessionId, mechanicId, shopId }),
    });

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        billingReadyForQuotes: !!data.billingReadyForQuotes,
      };
    }

    return {
      success: false,
      error: data.details ? `${data.error}: ${data.details}` : data.error,
    };
  } catch (error) {
    return { success: false, error: error.message || "Network error during setup sync" };
  }
}

export async function reconcileBillingSetup(mechanicId, shopId) {
  try {
    const response = await fetch(`${getApiBase()}/api/stripe/reconcile-billing-setup`, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({ mechanicId, shopId }),
    });

    const data = await response.json();

    if (response.ok) {
      return {
        success: !!data.billingReadyForQuotes,
        billingReadyForQuotes: !!data.billingReadyForQuotes,
        message: data.message,
      };
    }

    return { success: false, error: data.error || "Billing reconcile failed" };
  } catch (error) {
    return { success: false, error: error.message || "Network error during billing reconcile" };
  }
}

export async function pollForBillingReady({
  shopId,
  maxAttempts = 20,
  intervalMs = 500,
}) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    try {
      const response = await fetch(`${getApiBase()}/api/stripe/billing-status?shopId=${shopId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
      });

      if (!response.ok) continue;

      const data = await response.json();
      if (data.billingReadyForQuotes) {
        return { success: true, attempts: attempt };
      }
    } catch {
      // keep polling
    }
  }

  return { success: false, timeout: true };
}

/**
 * Feature branch: sync DB on Stripe return (webhooks not wired).
 * Preprod / production: poll for webhook only.
 */
export async function waitForBillingSetup({
  mechanicId,
  shopId,
  sessionId,
  onProgress,
}) {
  if (sessionId && isVercelFeatureBranchDeploy()) {
    onProgress?.({ phase: "syncing", message: "Confirming payment..." });
    const syncResult = await syncBillingSetupSession(sessionId, mechanicId, shopId);
    if (syncResult.success && syncResult.billingReadyForQuotes) {
      onProgress?.({ phase: "complete", message: "Payment method saved." });
      return { success: true, billingReadyForQuotes: true, method: "setup_session_sync" };
    }

    onProgress?.({ phase: "reconciling", message: "Syncing with Stripe..." });
    const reconcileResult = await reconcileBillingSetup(mechanicId, shopId);
    if (reconcileResult.success) {
      onProgress?.({ phase: "complete", message: "Payment method saved." });
      return {
        success: true,
        billingReadyForQuotes: true,
        method: "reconciliation",
      };
    }

    onProgress?.({ phase: "error", message: "Payment setup failed" });
    return {
      success: false,
      error:
        syncResult.error ||
        reconcileResult.error ||
        "Failed to save payment method on feature branch",
    };
  }

  if (sessionId) {
    onProgress?.({ phase: "polling", message: "Waiting for payment confirmation..." });
  } else {
    onProgress?.({ phase: "polling", message: "Waiting for billing update..." });
  }

  const pollResult = await pollForBillingReady({ shopId });
  if (pollResult.success) {
    onProgress?.({ phase: "complete", message: "Payment method saved." });
    return { success: true, billingReadyForQuotes: true, method: "webhook" };
  }

  onProgress?.({ phase: "error", message: "Payment setup failed" });
  return {
    success: false,
    error: "Payment confirmation timed out. Please refresh in a moment.",
  };
}

export function isBillingRequiredError(payload) {
  return payload?.code === "BILLING_REQUIRED" || payload?.code === "CARD_EXPIRED";
}

export function isRateAckRequiredError(payload) {
  return payload?.code === "RATE_ACK_REQUIRED";
}
